import { useMemo, useState, useRef } from "react";
import { htmlToJSON, isJSON, jsonToHTML } from "./htmlJsonHelper";

type Mode = "auto" | "html-to-json" | "json-to-html";

export default function MyHtmlJsonConverter() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("auto");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ---------------------------------- */
  /* Conversion Logic */
  /* ---------------------------------- */
  const output = useMemo(() => {
    if (!input.trim()) return "";

    try {
      setError(null);

      if (mode === "json-to-html") {
        const parsed = JSON.parse(input);
        return jsonToHTML(parsed);
      }

      if (mode === "html-to-json") {
        return JSON.stringify(htmlToJSON(input), null, 2);
      }

      // 🔁 AUTO MODE
      if (isJSON(input)) {
        return jsonToHTML(JSON.parse(input));
      }

      return JSON.stringify(htmlToJSON(input), null, 2);
    } catch (e: any) {
      setError(e.message || "Conversion failed");
      return "";
    }
  }, [input, mode]);

  /* ---------------------------------- */
  /* Actions */
  /* ---------------------------------- */
  const copyOutput = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    alert("Copied to clipboard");
  };

  const loadFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setInput(String(reader.result || ""));
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold">
        HTML ⇄ ProseMirror JSON Converter
      </h1>

      {/* 🔘 MODE TOGGLE */}
      <div className="flex flex-wrap gap-2">
        {(["auto", "html-to-json", "json-to-html"] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-3 py-1 rounded text-sm font-medium border
              ${
                mode === m
                  ? "bg-black text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
          >
            {m === "auto" && "Auto Detect"}
            {m === "html-to-json" && "HTML → JSON"}
            {m === "json-to-html" && "JSON → HTML"}
          </button>
        ))}
      </div>

      {/* 📂 LOAD FILE */}
      <div className="flex gap-3 items-center">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1 border rounded text-sm hover:bg-gray-100"
        >
          Load from file
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".html,.json,.txt"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) loadFromFile(file);
          }}
        />
      </div>

      {/* INPUT */}
      <div>
        <label className="block font-medium mb-2">
          Input
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={12}
          placeholder="<h1>Paste HTML</h1> OR { JSON }"
          className="w-full p-3 font-mono text-sm border rounded-lg"
        />
      </div>

      {/* ERROR */}
      {error && (
        <div className="text-red-600 text-sm">
          ⚠ {error}
        </div>
      )}

      {/* OUTPUT */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="font-medium">Converted Output</label>
          <button
            onClick={copyOutput}
            disabled={!output}
            className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
          >
            Copy
          </button>
        </div>

        <pre className="w-full p-4 bg-gray-100 rounded-lg text-xs overflow-auto max-h-[400px]">
          {output || "Converted output will appear here..."}
        </pre>
      </div>

      {/* INFO */}
      <div className="text-xs text-gray-500">
        • Runs fully in browser (Cloudflare-safe)<br />
        • HTML sanitized before conversion<br />
        • Supports TipTap / ProseMirror JSON
      </div>
    </div>
  );
}